import { useField, useFormikContext } from "formik";
import React from "react";
import ImageUpload from "shared/form/core/imageUpload";
import { useCopies } from "shared/hooks/useCopies";
import { DEFAULT_VALUE_FOR_EMPTY_FIELD, MAX_DEFAULT_IMAGE_SIZE_KB } from "shared/utils/constants";
import ErrorField from "../errorField";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import { FormImageDragFieldProps } from "../types";
import { getInstructionsForImageUpload } from "./utils";
import FormOverwriteField from "../overwriteField";
import { isEmpty } from "../../../utils";
import { getKBInMB } from "shared/utils";
import { SizeType } from "./enum";
import { ConflictType } from "shared/form/renderer/enums/Conflict";
import { useFormContextData } from "shared/form/context";
import { PreviewImage } from "shared/form/core/assetUpload/defaultAssetPreview";
import { TextAlignment } from "shared/enums/TextAlignment";

function FormImageDragField({ fullWidth = true, ...props }: FormImageDragFieldProps) {
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue, setFieldError } = useFormikContext();
  const [field, meta] = useField({ name: props.name, validate: props.validate });
  const copies = useCopies();

  const onChange = React.useCallback(
    async file => {
      await setFieldTouched(props.name, true);
      setFieldValue(props.name, file);
    },
    [props.name, setFieldTouched, setFieldValue]
  );

  const onError = React.useCallback(
    async errorMsg => {
      await setFieldTouched(props.name, true);
      setFieldError(props.name, errorMsg);
    },
    [props.name, setFieldError, setFieldTouched]
  );

  const instructions = React.useMemo(() => {
    const maxSize = props.instructions?.maxSize || MAX_DEFAULT_IMAGE_SIZE_KB;
    const sizeInMB = getKBInMB(maxSize);

    return getInstructionsForImageUpload({
      copies,
      maxSize: sizeInMB > 1 ? sizeInMB : maxSize,
      sizeType: sizeInMB > 1 ? SizeType.MB : SizeType.KB,
      aspectRatio: props.instructions?.aspectRatio,
      dimension: props.instructions?.dimension,
      acceptedFileTypes: props.acceptedFileTypes,
      supportedFormatCopyKey: props.supportedFormatCopyKey
    });
  }, [copies, props.instructions, props.acceptedFileTypes, props.supportedFormatCopyKey]);

  return (
    <FormGroup fullWidth={fullWidth}>
      <FormLabel
        label={props.label}
        required={props.required}
        additionalLabel={instructions}
        infoLabel={props.infoLabel}
        warningText={props.warningText}
      />
      {formContextData.isReadOnly ? (
        field.value ? (
          <PreviewImage url={field.value} dimension={props.instructions?.dimension} align={TextAlignment.LEFT} />
        ) : (
          DEFAULT_VALUE_FOR_EMPTY_FIELD
        )
      ) : (
        <ImageUpload
          {...field}
          {...props}
          handleChange={onChange}
          handleError={onError}
          disabled={props.disabled}
          hasError={meta.touched && Boolean(meta.error)}
          exactPreview={props.instructions?.exactPreview}
          dimension={props.instructions?.dimension}
        />
      )}

      <ErrorField meta={meta} />
      {!isEmpty(props.conflictValue) && (
        <FormOverwriteField forFieldName={props.name} disabled={props.disabled} conflictType={ConflictType.IMAGE} />
      )}
    </FormGroup>
  );
}

export default FormImageDragField;
